// web/src/components/TournamentBracketModal.tsx
import React from "react";
import OverlayPortal from "./OverlayPortal";

interface BracketParticipant {
  id: number | null;
  user_id: number | null;
  username: string | null;
  status?: string;
  final_placement?: number | null;
}

export interface BracketMatch {
  id: number;
  round_number: number;
  match_number: number;
  best_of: number;
  status: string;
  winner_entry_id: number | null;
  entry1: BracketParticipant | undefined;
  entry2: BracketParticipant | undefined;
  series_score?: {
    entry1_wins: number;
    entry2_wins: number;
    wins_needed: number;
    games_played: number;
  };
}

export interface BracketRound {
  round_number: number;
  matches: BracketMatch[];
}

export interface TournamentBracketData {
  tournament_id: number;
  tournament_name?: string;
  status?: string;
  format_type?: string;
  rounds: BracketRound[];
}

interface TournamentBracketModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentName?: React.ReactNode;
  bracketData: TournamentBracketData | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  currentUserId?: number;
}

/**
 * Utility: normalize and sort rounds/matches
 */
function prepareRounds(data: TournamentBracketData | null): BracketRound[] {
  if (!data || !data.rounds) return [];
  const rounds = [...data.rounds].sort((a, b) => a.round_number - b.round_number);
  return rounds.map((r) => ({
    ...r,
    matches: [...r.matches].sort((a, b) => a.match_number - b.match_number),
  }));
}

function getRoundLabel(roundNumber: number, totalRounds: number): string {
  if (!totalRounds) return `Round ${roundNumber}`;
  const roundsLeft = totalRounds - roundNumber;
  if (roundsLeft === 0) return "Finals";
  if (roundsLeft === 1) return "Semifinals";
  if (roundsLeft === 2) return "Quarterfinals";
  if (roundsLeft === 3 && totalRounds >= 5) return "Round of 16";
  if (roundsLeft === 4) return "Round of 32";
  return `Round ${roundNumber}`;
}

/**
 * Single SVG player box
 */
function PlayerBox(props: {
  x: number;
  y: number;
  width: number;
  height: number;
  participant?: BracketParticipant;
  isWinner: boolean;
  isCurrentUser: boolean;
}) {
  const { x, y, width, height, participant, isWinner, isCurrentUser } = props;
  const name = participant?.username || (participant ? `Player ${participant.id}` : "Bye");

  const strokeWidth = isCurrentUser ? 2.5 : 1.5;
  const strokeColor = isCurrentUser ? "#b45309" : "#e5b88a";
  const fillColor = isWinner ? "#ffecc7" : "#fff7e3";

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={6}
        ry={6}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />
      <text
        x={x + 8}
        y={y + height / 2 + 4}
        fontSize={12}
        fontWeight={isWinner ? 700 : 500}
        fill="#3e2615"
      >
        {name}
      </text>
      {participant?.final_placement != null && (
        <text
          x={x + width - 16}
          y={y + height / 2 + 4}
          fontSize={11}
          textAnchor="end"
          fill="#8b6542"
        >
          #{participant.final_placement}
        </text>
      )}
    </g>
  );
}

/**
 * SVG bracket for a single-elimination bracket.
 * Layout rules:
 *  - First round is split: left half → left column, right half → right column
 *  - Last round is rendered in the center as "Finals"
 *  - Each match is rendered as TWO stacked PlayerBox elements
 *  - Horizontal/vertical connector lines link players → match → final
 */
function SvgBracket({
  bracketData,
  currentUserId,
}: {
  bracketData: TournamentBracketData;
  currentUserId?: number;
}) {
  const rounds = prepareRounds(bracketData);
  if (rounds.length === 0) {
    return (
      <div style={{ padding: "8px", color: "#3e2615" }}>
        Bracket data not available for this tournament.
      </div>
    );
  }

  const totalRounds = rounds.length;
  const finalRound = rounds[totalRounds - 1];
  const sideRounds = rounds.slice(0, -1); // all rounds except final

  // Split each non-final round into left/right halves (by match order)
  const leftSideRounds: BracketMatch[][] = [];
  const rightSideRounds: BracketMatch[][] = [];
  sideRounds.forEach((round) => {
    const matches = round.matches;
    const half = Math.ceil(matches.length / 2);
    const left = matches.slice(0, half);
    const right = matches.slice(half);
    leftSideRounds.push(left);
    rightSideRounds.push(right);
  });

  // Layout constants
  const PLAYER_WIDTH = 160;
  const PLAYER_HEIGHT = 28;
  const PLAYER_GAP_Y = 8; // vertical gap between the two players of a match
  const MATCH_VERTICAL_SPACING = PLAYER_HEIGHT * 2 + PLAYER_GAP_Y + 32; // spacing between match centers in same round
  const COLUMN_GAP = 80;

  const LEFT_START_X = 80;
  const CENTER_X = LEFT_START_X + PLAYER_WIDTH + COLUMN_GAP; // where finals card goes
  const RIGHT_START_X = CENTER_X + PLAYER_WIDTH + COLUMN_GAP;

  const TOP_PADDING = 100;

  /**
   * For each side and each round, compute Y positions for matches.
   * We'll stack matches vertically with consistent spacing.
   */
  const computeMatchYs = (roundMatches: BracketMatch[][]) => {
    const positions: { [matchId: number]: { centerY: number } } = {};
    if (roundMatches.length === 0) return positions;

    const firstRound = roundMatches[0];
    firstRound.forEach((match, idx) => {
      const centerY = TOP_PADDING + idx * MATCH_VERTICAL_SPACING;
      positions[match.id] = { centerY };
    });

    // Higher rounds: center between children from previous round
    for (let r = 1; r < roundMatches.length; r++) {
      const prevRound = roundMatches[r - 1];
      const curRound = roundMatches[r];
      curRound.forEach((match, idx) => {
        // children approx indices 2*idx and 2*idx+1 from previous round
        const child1 = prevRound[2 * idx];
        const child2 = prevRound[2 * idx + 1] || prevRound[2 * idx];
        const c1y = child1 ? positions[child1.id]?.centerY ?? TOP_PADDING : TOP_PADDING;
        const c2y = child2 ? positions[child2.id]?.centerY ?? c1y : c1y;
        positions[match.id] = { centerY: (c1y + c2y) / 2 };
      });
    }

    return positions;
  };

  const leftYs = computeMatchYs(leftSideRounds);
  const rightYs = computeMatchYs(rightSideRounds);

  // Final round Y (centered across both sides)
  let finalCenterY = TOP_PADDING;
  if (finalRound.matches.length > 0) {
    const leftTopMatch = leftSideRounds[leftSideRounds.length - 1]?.[0];
    const rightTopMatch = rightSideRounds[rightSideRounds.length - 1]?.[0];
    if (leftTopMatch && rightTopMatch) {
      const ly = leftYs[leftTopMatch.id]?.centerY ?? TOP_PADDING;
      const ry = rightYs[rightTopMatch.id]?.centerY ?? TOP_PADDING;
      finalCenterY = (ly + ry) / 2;
    } else if (leftTopMatch) {
      finalCenterY = leftYs[leftTopMatch.id]?.centerY ?? TOP_PADDING;
    } else if (rightTopMatch) {
      finalCenterY = rightYs[rightTopMatch.id]?.centerY ?? TOP_PADDING;
    }
  }

  const svgHeight =
    Math.max(
      TOP_PADDING + (leftSideRounds[0]?.length || 1) * MATCH_VERTICAL_SPACING,
      TOP_PADDING + (rightSideRounds[0]?.length || 1) * MATCH_VERTICAL_SPACING,
      finalCenterY + 80
    ) + 40;
  const svgWidth = RIGHT_START_X + PLAYER_WIDTH + 40;

  /**
   * Render one side (left or right).
   * For the right side we mirror horizontally:
   *  - columns move from right → left as rounds advance
   *  - connectors come out of the LEFT side of the boxes
   */
  const renderSideMatches = (
    side: "left" | "right",
    sideRoundsMatches: BracketMatch[][],
    positions: { [matchId: number]: { centerY: number } },
    xBase: number,
    connectToFinal: boolean
  ) => {
    const groups: React.ReactNode[] = [];

    sideRoundsMatches.forEach((roundMatches, roundIdx) => {
      const x =
        side === "left"
          ? xBase + roundIdx * (PLAYER_WIDTH + COLUMN_GAP)
          : xBase - roundIdx * (PLAYER_WIDTH + COLUMN_GAP);

      // Label only the round closest to the final on each side
      if (roundIdx === sideRoundsMatches.length - 1) {
        const roundNumber = sideRounds[roundIdx].round_number;
        groups.push(
          <text
            key={`${side}-${roundIdx}-label`}
            x={x + PLAYER_WIDTH / 2}
            y={32}
            fontSize={16}
            fontWeight={700}
            fill="#3e2615"
            textAnchor="middle"
          >
            {getRoundLabel(roundNumber, totalRounds)}
          </text>
        );
      }

      roundMatches.forEach((match, idxInRound) => {
        const centerY = positions[match.id]?.centerY ?? TOP_PADDING;
        const boxTotalHeight = PLAYER_HEIGHT * 2 + PLAYER_GAP_Y;
        const boxTopY = centerY - boxTotalHeight / 2;

        const p1Y = boxTopY;
        const p2Y = boxTopY + PLAYER_HEIGHT + PLAYER_GAP_Y;
        const p1MidY = p1Y + PLAYER_HEIGHT / 2;
        const p2MidY = p2Y + PLAYER_HEIGHT / 2;

        // Where the local vertical joint sits for this match
        const joinX = side === "left" ? x + PLAYER_WIDTH + 12 : x - 12;
        const jointFromX = side === "left" ? x + PLAYER_WIDTH : x; // where horizontal from box starts

        const joinTopY = p1MidY;
        const joinBotY = p2MidY;
        const joinMidY = (joinTopY + joinBotY) / 2;

        const winnerId = match.winner_entry_id;
        const isEntry1Winner = !!winnerId && winnerId === (match.entry1?.id ?? null);
        const isEntry2Winner = !!winnerId && winnerId === (match.entry2?.id ?? null);

        const userInEntry1 = !!currentUserId && match.entry1?.user_id === currentUserId;
        const userInEntry2 = !!currentUserId && match.entry2?.user_id === currentUserId;

        groups.push(
          <g key={`${side}-match-${match.id}`}>
            {/* Player boxes */}
            <PlayerBox
              x={x}
              y={p1Y}
              width={PLAYER_WIDTH}
              height={PLAYER_HEIGHT}
              participant={match.entry1}
              isWinner={isEntry1Winner}
              isCurrentUser={userInEntry1}
            />
            <PlayerBox
              x={x}
              y={p2Y}
              width={PLAYER_WIDTH}
              height={PLAYER_HEIGHT}
              participant={match.entry2}
              isWinner={isEntry2Winner}
              isCurrentUser={userInEntry2}
            />

            {/* Local match connector (entry1 & entry2 → join point) */}
            <line
              x1={jointFromX}
              y1={p1MidY}
              x2={joinX}
              y2={p1MidY}
              stroke="#d5b083"
              strokeWidth={1.5}
            />
            <line
              x1={jointFromX}
              y1={p2MidY}
              x2={joinX}
              y2={p2MidY}
              stroke="#d5b083"
              strokeWidth={1.5}
            />
            <line
              x1={joinX}
              y1={joinTopY}
              x2={joinX}
              y2={joinBotY}
              stroke="#d5b083"
              strokeWidth={1.5}
            />

            {/* Score label at the joint (avoid overlapping the boxes) */}
            {match.series_score &&
              (match.series_score.entry1_wins > 0 || match.series_score.entry2_wins > 0) && (
                <text
                  x={joinX + (side === "left" ? 4 : -4)}
                  y={joinTopY - 4}
                  fontSize={11}
                  fill="#8b6542"
                  textAnchor={side === "left" ? "start" : "end"}
                >
                  {match.series_score.entry1_wins}-{match.series_score.entry2_wins}
                </text>
              )}

            {/* Connections to *next* round on the same side */}
            {roundIdx < sideRoundsMatches.length - 1 && (() => {
              const nextRound = sideRoundsMatches[roundIdx + 1];
              const parentIdx = Math.floor(idxInRound / 2);
              const parentMatch = nextRound[parentIdx];
              if (!parentMatch) return null;

              const parentCenter = positions[parentMatch.id]?.centerY ?? centerY;
              const parentX =
                side === "left"
                  ? xBase + (roundIdx + 1) * (PLAYER_WIDTH + COLUMN_GAP)
                  : xBase - (roundIdx + 1) * (PLAYER_WIDTH + COLUMN_GAP);
              const parentBoxTop = parentCenter - boxTotalHeight / 2;
              const parentP1MidY = parentBoxTop + PLAYER_HEIGHT / 2;
              const parentP2MidY =
                parentBoxTop + PLAYER_HEIGHT / 2 + PLAYER_HEIGHT + PLAYER_GAP_Y;
              const parentJoinMidY = (parentP1MidY + parentP2MidY) / 2;
              const parentJoinX = side === "left" ? parentX - 12 : parentX + 12;

              return (
                <g>
                  <line
                    x1={joinX}
                    y1={joinMidY}
                    x2={parentJoinX}
                    y2={joinMidY}
                    stroke="#d5b083"
                    strokeWidth={1.5}
                  />
                  <line
                    x1={parentJoinX}
                    y1={joinMidY}
                    x2={parentJoinX}
                    y2={parentJoinMidY}
                    stroke="#d5b083"
                    strokeWidth={1.5}
                  />
                </g>
              );
            })()}

            {/* Last side round connects into the final */}
            {roundIdx === sideRoundsMatches.length - 1 &&
              connectToFinal &&
              finalRound.matches[0] &&
              (() => {
                const finalMatch = finalRound.matches[0];
                const finalBoxTotal = PLAYER_HEIGHT * 2 + PLAYER_GAP_Y;
                const finalTop = finalCenterY - finalBoxTotal / 2;
                const finalP1MidY = finalTop + PLAYER_HEIGHT / 2;
                const finalP2MidY =
                  finalTop + PLAYER_HEIGHT / 2 + PLAYER_HEIGHT + PLAYER_GAP_Y;
                const finalJoinMidY = (finalP1MidY + finalP2MidY) / 2;

                const connectEndX =
                  side === "left" ? CENTER_X - 12 : CENTER_X + PLAYER_WIDTH + 12;

                return (
                  <g>
                    <line
                      x1={joinX}
                      y1={joinMidY}
                      x2={connectEndX}
                      y2={joinMidY}
                      stroke="#d5b083"
                      strokeWidth={1.5}
                    />
                    <line
                      x1={connectEndX}
                      y1={joinMidY}
                      x2={connectEndX}
                      y2={finalJoinMidY}
                      stroke="#d5b083"
                      strokeWidth={1.5}
                    />
                  </g>
                );
              })()}
          </g>
        );
      });
    });

    return groups;
  };

  const finalMatch = finalRound.matches[0];

  return (
    <svg
      width="100%"
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      style={{ maxHeight: "70vh", display: "block" }}
    >
      {/* Left side */}
      {renderSideMatches("left", leftSideRounds, leftYs, LEFT_START_X, true)}

      {/* Finals in the center */}
      {finalMatch && (
        <g>
          <text
            x={CENTER_X + PLAYER_WIDTH / 2}
            y={32}
            fontSize={16}
            fontWeight={700}
            fill="#3e2615"
            textAnchor="middle"
          >
            {getRoundLabel(finalMatch.round_number, totalRounds)}
          </text>
          {(() => {
            const boxTotalHeight = PLAYER_HEIGHT * 2 + PLAYER_GAP_Y;
            const boxTopY = finalCenterY - boxTotalHeight / 2;
            const p1Y = boxTopY;
            const p2Y = boxTopY + PLAYER_HEIGHT + PLAYER_GAP_Y;

            const winnerId = finalMatch.winner_entry_id;
            const isEntry1Winner =
              !!winnerId && winnerId === (finalMatch.entry1?.id ?? null);
            const isEntry2Winner =
              !!winnerId && winnerId === (finalMatch.entry2?.id ?? null);
            const userInEntry1 =
              !!currentUserId && finalMatch.entry1?.user_id === currentUserId;
            const userInEntry2 =
              !!currentUserId && finalMatch.entry2?.user_id === currentUserId;

            return (
              <g>
                <PlayerBox
                  x={CENTER_X}
                  y={p1Y}
                  width={PLAYER_WIDTH}
                  height={PLAYER_HEIGHT}
                  participant={finalMatch.entry1}
                  isWinner={isEntry1Winner}
                  isCurrentUser={userInEntry1}
                />
                <PlayerBox
                  x={CENTER_X}
                  y={p2Y}
                  width={PLAYER_WIDTH}
                  height={PLAYER_HEIGHT}
                  participant={finalMatch.entry2}
                  isWinner={isEntry2Winner}
                  isCurrentUser={userInEntry2}
                />
              </g>
            );
          })()}
        </g>
      )}

      {/* Right side (mirrored) */}
      {renderSideMatches("right", rightSideRounds, rightYs, RIGHT_START_X, true)}
    </svg>
  );
}

export default function TournamentBracketModal({
  isOpen,
  onClose,
  tournamentName,
  bracketData,
  loading,
  error,
  onRetry,
  currentUserId,
}: TournamentBracketModalProps) {
  if (!isOpen) return null;

  return (
    <OverlayPortal>
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.65)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
          padding: "20px",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "#fffaeb",
            border: "3px solid #8b6542",
            borderRadius: "20px",
            boxShadow: "0 12px 30px rgba(0,0,0,0.35)",
            width: "min(1200px, 95vw)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 20px",
              borderBottom: "2px solid #e7c9a5",
              background: "linear-gradient(90deg, #f7b267 0%, #f4a261 100%)",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
            }}
          >
            <div>
              <div style={{ fontSize: "20px", fontWeight: 800, color: "#3e2615" }}>
                {tournamentName || bracketData?.tournament_name || "Tournament Bracket"}
              </div>
              {bracketData?.status && (
                <div
                  style={{ color: "#3e2615", opacity: 0.8, fontSize: "13px" }}
                >
                  Status: {bracketData.status}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "2px solid #8b6542",
                background: "#fffaeb",
                color: "#3e2615",
                fontWeight: 800,
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
              }}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "16px", overflow: "hidden" }}>
            {loading && (
              <div
                className="text-center"
                style={{ color: "#3e2615", fontWeight: 600 }}
              >
                Loading bracket...
              </div>
            )}

            {error && !loading && (
              <div
                className="flex flex-col items-center gap-3"
                style={{ color: "#3e2615" }}
              >
                <div style={{ fontWeight: 700 }}>
                  Unable to load bracket right now.
                </div>
                <div style={{ fontSize: "14px", opacity: 0.9 }}>{error}</div>
                <button
                  onClick={onRetry}
                  style={{
                    padding: "10px 16px",
                    borderRadius: "10px",
                    border: "2px solid #8b6542",
                    background: "#fffaeb",
                    color: "#3e2615",
                    fontWeight: 700,
                    boxShadow: "0 2px 0 #8b6542, 0 6px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && bracketData && (
              <div
                style={{
                  borderRadius: "12px",
                  background: "#fff7e3",
                  border: "1px solid #e7c9a5",
                  padding: "8px",
                  overflow: "auto",
                }}
              >
                <SvgBracket
                  bracketData={bracketData}
                  currentUserId={currentUserId}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
